import React, { useEffect, useState, useCallback } from 'react';
import './AdminConsole.css';
import API from '../../API';
import { useAuth0 } from '@auth0/auth0-react';
import AppHeader from '../AppHeader';
import EditEvent from './EditEvent';
import EditDeeplink from './EditDeeplink';
import AddNewEvent from './AddNewEvent';
// import EditServerInfo from './EditServerInfo';
// import DesktopClient from './DesktopClient.jsx';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import StorefrontManagement from './StorefrontManagement';
import WebGPUBuilds from './WebGPUManagement/WebGPUBuilds';
import EventDefaults from './EventDefaults';

// TODO: Rename Deeplink page & move DesktopClient to Active Events?

const AdminConsole = ({ wallets, setWallets }) => {
  const [events, setEvents] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [builds, setBuilds] = useState([]);
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();

  async function onSetActive(info) {
    const response = await API.adminUpdateWebGPUBuildActive(accessToken, info);
    if (response.status !== 204) {
      console.log('Error updating active build.');
      return;
    }
  }

  async function onDelete(info) {
    const response = await API.adminDeleteWebGPUBuild(accessToken, info);
    if (response.status !== 204) {
      console.log('Error deleting build.');
      return;
    }
  }

  async function onCreate(info, file) {
    const presignedUrlResponse = await API.adminGetWebGPUBuildPresignedUrl(accessToken, info);
    if (presignedUrlResponse.status !== 200) {
      console.log('Error getting presigned url');
      return;
    }

    const data = await presignedUrlResponse.json();

    const s3Response = await fetch(data.presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/zip'
      },
      body: file
    });
    if (s3Response.status !== 200) {
      console.log('Failed to upload the zip file');
      return;
    }

    const response = await API.adminCreateWebGPUBuild(accessToken, info);
    if (response.status !== 200) {
      console.log('Error creating build.');
      return;
    }
  }

  const fetchEvents = useCallback(async () => {
    if (isAuthenticated) {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'ffclient:play'
      });
      setAccessToken(token);

      const response = await API.adminGetEvents(token);
      if (response.status !== 200) {
        console.log('Error getting events.');
        return;
      }

      const data = await response.json();
      setEvents(data.events);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    async function getBuilds() {
      if (isAuthenticated) {
        const accessToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'ffclient:play'
        });
        setAccessToken(accessToken);
        const response = await API.adminGetWebGPUBuilds(accessToken);
        if (response.status !== 200) {
          console.log('Error getting builds.');
          return;
        }

        const data = await response.json();
        setBuilds(data.builds);
      }
    }

    async function getActiveVersion() {
      if (isAuthenticated) {
        const accessToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'ffclient:play'
        });
        setAccessToken(accessToken);

        const response = await API.adminGetWebGPUBuildActive(accessToken);
        if (response.status !== 200) {
          console.log('Error getting active build.');
          return;
        }

        const data = await response.json();
        setActiveVersion(data.build.version);
      }
    }

    fetchEvents();
    getBuilds();
    getActiveVersion();
  }, [getAccessTokenSilently, isAuthenticated, fetchEvents]);

  if (isLoading) {
    return <div>Loading ...</div>;
  }

  // Filter events owned by the current user
  const myEvents = events.filter((event) => event.host_id === user?.sub);

  return (
    isAuthenticated && (
      <div className="fullscreen">
        <AppHeader wallets={wallets} setWallets={setWallets} />
        <Tabs
          disableUpDownKeys={true}
          className="admin-console"
          selectedTabClassName="admin-console__tab--selected"
          selectedTabPanelClassName="admin-console__tab-panel--selected"
        >
          <TabList className="admin-console__tab-list">
            <Tab className="admin-console__tab">My Events</Tab>
            <Tab className="admin-console__tab">All Events</Tab>
            <Tab className="admin-console__tab">Launcher</Tab>
            {/* <Tab className="admin-console__tab">Server Info</Tab> */}
            {/* <Tab className="admin-console__tab">Desktop Clients</Tab> */}
            <Tab className="admin-console__tab">Manage Storefront</Tab>
            <Tab className="admin-console__tab">Manage Web GPU Builds</Tab>
            <Tab className="admin-console__tab">Defaults</Tab>
          </TabList>
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">My Events</h2>
            <br />
            <AddNewEvent
              accessToken={accessToken}
              userId={user?.sub}
              onEventCreated={fetchEvents}
            />
            {myEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-400 border border-dashed border-gray-600 rounded-xl">
                <p className="text-lg mb-2">You don't have any events yet.</p>
                <p className="text-sm">Click "Create New Event" above to get started.</p>
              </div>
            ) : (
              myEvents.map((event) => (
                <EditEvent
                  event={event}
                  accessToken={accessToken}
                  onEventDeleted={fetchEvents}
                  key={event.id}
                />
              ))
            )}
          </TabPanel>
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">All Events</h2>
            <br />
            <AddNewEvent
              accessToken={accessToken}
              userId={user?.sub}
              onEventCreated={fetchEvents}
            />
            {events.map((event) => (
              <EditEvent
                event={event}
                accessToken={accessToken}
                onEventDeleted={fetchEvents}
                key={event.id}
              />
            ))}
          </TabPanel>
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Launcher</h2>
            <br />
            <EditDeeplink accessToken={accessToken} />
          </TabPanel>
          {/* <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Server Info</h2>
            <br />
            <EditServerInfo accessToken={accessToken} />
          </TabPanel> */}
          {/* <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Desktop Clients</h2>
            <br />
            <DesktopClient accessToken={accessToken} />
          </TabPanel> */}
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Storefront Management</h2>
            <br />
            <StorefrontManagement accessToken={accessToken} />
          </TabPanel>
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Web GPU Management</h2>
            <br />
            <WebGPUBuilds
              builds={builds}
              current_user_id={user.sub}
              current_user_username={user['https://futurefest.io/username']}
              activeVersion={activeVersion}
              onCreate={onCreate}
              onDelete={onDelete}
              onSetActive={onSetActive}
            />
          </TabPanel>
          <TabPanel className="admin-console__tab-panel">
            <h2 className="ff-header">Defaults</h2>
            <br />
            <EventDefaults accessToken={accessToken} />
          </TabPanel>
        </Tabs>
      </div>
    )
  );
};

export default AdminConsole;
